/**
 * Left sidebar navigation. Renders the nav item set for the current user's role
 * (NAV_BY_ROLE) plus an account button. When on a data-room route, expands an
 * inline sub-list of categories pulled from DataRoomContext.
 * Commented-out items (CEG Funding) are intentionally hidden, not dead code.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataRoom } from '../context/DataRoomContext';
import cegLogo from '../assets/CEG Logo.svg';

// ─── Icons ─────────────────────────────────────────────────────────────────────

function IconDashboard() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconDataRoom() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function IconFundraising() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCRM() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPeople() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconFiles() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="17" x2="12" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="8" y="2" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
      <path d="M8 3H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 12h6M9 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconGrant() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <line x1="12" y1="12" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="10" y1="14" x2="14" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}


// ─── Nav items by role ──────────────────────────────────────────────────────────

const NAV_BY_ROLE = {
  founder: [
    { path: '/dashboard',   label: 'Dashboard',        Icon: IconDashboard  },
    { path: '/data-room',   label: 'Data Room',         Icon: IconDataRoom   },
    { path: '/fundraising', label: 'Fundraising',       Icon: IconFundraising }, 
    { path: '/crm',         label: 'Investor Hygiene',  Icon: IconCRM        },
    // { path: '/funding',     label: 'CEG Funding',       Icon: IconGrant      },
    { path: '/files',       label: 'Files',             Icon: IconFiles      },
  ],
  eir: [
    { path: '/eir',             label: 'My Founders',      Icon: IconDashboard   },
    { path: '/eir/connections', label: 'Connections',      Icon: IconPeople      },
    { path: '/eir/data-room',   label: 'Data Rooms',       Icon: IconDataRoom    },
    { path: '/eir/fundraising', label: 'Fundraising',      Icon: IconFundraising },
    { path: '/eir/crm',         label: 'Investor Hygiene', Icon: IconCRM         },
    { path: '/eir/files',       label: 'Files',            Icon: IconFiles       },
  ],
  ceg: [
    { path: '/ceg',       label: 'Program Overview', Icon: IconDashboard },
  ],
  admin: [
    { path: '/admin',              label: 'Program Overview', Icon: IconDashboard },
    { path: '/admin/accounts',     label: 'Accounts',         Icon: IconSettings  },
    { path: '/admin/applications', label: 'Applications',     Icon: IconClipboard },
    { path: '/admin/connections',  label: 'Connections',      Icon: IconCRM       },
    { path: '/eir',                label: 'EIR View',         Icon: IconPeople    },
    // { path: '/admin/funding-applications', label: 'Funding',    Icon: IconGrant     },
  ],
};

// ─── Component ─────────────────────────────────────────────────────────────────

// User avatar that falls back to a generic icon if the image fails to load.
// avatarVersion is appended as a cache-busting query param so a freshly
// uploaded avatar re-fetches instead of serving the stale cached image.
function NavAvatar({ userId, avatarVersion }) {
  const [failed, setFailed] = useState(false);

  // Reset the error state when the user or avatar version changes so a new
  // upload gets a fresh load attempt.
  useEffect(() => { setFailed(false); }, [userId, avatarVersion]);

  if (failed) return <IconUser />;
  return (
    <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#fff', overflow: 'hidden', flexShrink: 0 }}>
      <img
        src={`/api/vr/account/avatar/${userId}?v=${avatarVersion}`}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, avatarVersion } = useAuth();
  const { categories } = useDataRoom();

  const navItems = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.founder;

  // The category id currently open in the data-room sub-nav, parsed from the URL.
  const activeCatId = (() => {
    const m = location.pathname.match(/^\/data-room\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  })();

  // Admin and EIR roots require an exact match (their children are distinct
  // top-level items); other items also match nested sub-paths.
  function isActive(path) {
    if (path.startsWith('/admin') || path === '/eir') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }

  return (
    <nav style={styles.nav} className="nav-sidebar">
      <a href="https://ceg.org" target="_blank" rel="noreferrer" style={styles.logo} className="nav-logo" aria-label="CEG website">
        <img src={cegLogo} alt="CEG" style={styles.logoImg} />
      </a>

      <div style={styles.navItems} className="nav-items">
        {navItems.map(({ path, label, Icon }) => {
          const active = isActive(path);
          const isDataRoom = path === '/data-room';
          const showSub = isDataRoom && categories.length > 0 && location.pathname.startsWith('/data-room');
          return (
            <div key={path}>
              <button
                style={{ ...styles.navBtn, ...(active ? styles.navBtnActive : {}) }}
                onClick={() => navigate(path)}
                aria-label={label}
              >
                <Icon />
                <span style={styles.navLabel}>{label}</span>
              </button>
              {showSub && (
                <ul style={styles.subList}>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        style={{ ...styles.subItem, ...(activeCatId === cat.id ? styles.subItemActive : {}) }}
                        onClick={() => navigate(`/data-room/${cat.id}`)}
                      >
                        <span style={styles.subDot}>·</span>
                        {cat.id}. {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <button
        style={{ ...styles.userBtn, ...(isActive('/account') ? styles.navBtnActive : {}) }}
        className="nav-user-btn"
        onClick={() => navigate('/account')}
        aria-label="My Account"
      >
        {user ? <NavAvatar userId={user.id} avatarVersion={avatarVersion} /> : <IconUser />}
        <span style={styles.navLabel}>{user ? `${user.first_name} ${user.last_name}` : 'My Account'}</span>
      </button>
    </nav>
  );
}

const styles = {
  nav: {
    width: 'var(--nav-width)',
    minWidth: 'var(--nav-width)',
    background: 'var(--color-forest)',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingTop: 16,
    paddingBottom: 16,
    flexShrink: 0,
    zIndex: 100,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 16,
    padding: '8px 16px',
  },
  logoImg: {
    width: 48,
    height: 48,
    objectFit: 'contain',
  },
  navItems: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 2,
    paddingTop: 8,
    paddingLeft: 10,
    paddingRight: 10,
  },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    width: '100%',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    color: 'rgba(255,255,255,0.65)',
    transition: 'background 0.15s, color 0.15s',
    textAlign: 'left',
  },
  navBtnActive: {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
  },
  navLabel: {
    fontSize: 20,
    fontFamily: 'var(--font-body)',
    fontWeight: 100,
    letterSpacing: '0.01em',
    whiteSpace: 'normal',
    lineHeight: 1.2,
  },
  subList: {
    listStyle: 'none',
    padding: '2px 0 4px 36px',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  subItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    padding: '6px 10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    color: 'rgba(255,255,255,0.55)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    fontWeight: 400,
    textAlign: 'left',
    lineHeight: 1.3,
    transition: 'background 0.12s, color 0.12s',
  },
  subItemActive: {
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    fontWeight: 600,
  },
  subDot: {
    opacity: 0.4,
    fontSize: 16,
    lineHeight: 1,
    flexShrink: 0,
  },
  userBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    color: 'rgba(255,255,255,0.8)',
    marginLeft: 10,
    marginRight: 10,
  },
};
